import type { ProgramPortfolio } from '@/modules/Entertainment/interface/Program';
import Image from 'next/image';
import { useMemo, type FC } from 'react';
import { Text } from '@eqi-investimentos/react';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { SearchPortfolioAsset } from '@/modules/Search/interface/Search';
import { getTableHeads, getTableRows } from '@/utils/table';

export const RecommendedWalletItem: FC<RecommendedWalletItemProps> = ({
  assets,
  title,
}) => {
  const recommendations = useMemo(
    () => organizeRecommendations(assets),

    [assets]
  );

  return (
    <AccordionItem border="none">
      <AccordionButton
        display="flex"
        justifyContent="space-between"
        p="s"
        bgColor="background.brandLight"
        borderRadius="2xs"
        _hover={{ bgColor: 'background.hover.brandLight' }}
        _expanded={{
          borderBottomRadius: 'none',
        }}
      >
        <Text size="xl">{title}</Text>
        <AccordionIcon w={28} h={28} color="content.brand" />
      </AccordionButton>
      <AccordionPanel
        p="0"
        border="1px solid"
        borderColor="border.secondary"
        borderTop="none"
        borderBottomRadius="2xs"
      >
        <TableContainer
          py="1.875rem"
          px="0.75rem"
          overflowX="scroll"
          sx={{
            '&::-webkit-scrollbar': {
              height: '0.3125rem',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'background.brandLight',
              borderRadius: '0.625rem',
            },
          }}
        >
          <Table variant="simple" rowGap="l" columnGap="6xl">
            <Thead>
              <Tr>
                {recommendations.heads?.map(({ cell }, idx) => (
                  <Th
                    key={idx}
                    py="0.625rem"
                    px="1.75rem"
                    fontSize="l"
                    fontWeight="medium"
                    border="transparent"
                    color="content.primary"
                  >
                    {cell}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {recommendations.rows?.map((row, idx) => (
                <Tr key={idx} borderColor="transparent">
                  {row.cells.map(({ styleProps, thumb, value }, idx) => (
                    <Td
                      key={idx}
                      minW="2.875rem"
                      py="0.625rem"
                      px="1.75rem"
                      fontWeight="medium"
                      fontSize="l"
                      border="transparent"
                      sx={{
                        '& img': { w: '2.0625rem', h: '2.0625rem' },
                        ...styleProps,
                      }}
                    >
                      {thumb ? (
                        <Image
                          src={thumb ?? ''}
                          alt={value ?? ''}
                          width={33}
                          height={33}
                          style={{ objectFit: 'cover' }}
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = '/assets/eqi-placeholder.png';
                          }}
                        />
                      ) : (
                        value
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </AccordionPanel>
    </AccordionItem>
  );
};

const organizeRecommendations = (assets: SearchPortfolioAsset[]) => {
  if (!assets) return {};
  return {
    heads: getTableHeads(assets),
    rows: getTableRows(assets),
  };
};

type RecommendedWalletItemProps = Pick<ProgramPortfolio, 'assets' | 'title'>;
